import React, { useState, useEffect } from "react";
import {
  FileText,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { adminReportService } from "@/services/api/admin.report.service";

const UserReportGenerator = ({ userId, userName }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [format, setFormat] = useState("PDF");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  // Format options for the report
  const formatOptions = [
    { value: "PDF", label: "PDF Document" },

    { value: "EXCEL", label: "Excel Spreadsheet" },
  ];

  // Clean up polling interval when component unmounts
  useEffect(() => {
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [pollingInterval]);

  // Reset error when form inputs change
  useEffect(() => {
    setError(null);
  }, [startDate, endDate, format]);

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setReportData(null);

    try {
      // Validate dates
      if (!startDate || !endDate) {
        throw new Error("Start date and end date are required");
      }

      if (new Date(startDate) > new Date(endDate)) {
        throw new Error("Start date cannot be after end date");
      }

      // Call API to generate report
      const response = await adminReportService.generateUserReport(
        userId,
        startDate,
        endDate,
        format
      );

      setReportData(response.data);

      // Start polling for report status if report is queued or processing
      if (
        response.data.status === "QUEUED" ||
        response.data.status === "PROCESSING"
      ) {
        startPolling(response.data.reportId);
      }
    } catch (err) {
      setError(err.message || "Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (reportId) => {
    // Clear any existing polling
    if (pollingInterval) clearInterval(pollingInterval);

    // Poll every 3 seconds
    const interval = setInterval(async () => {
      try {
        const response = await adminReportService.getReportStatus(reportId);

        // Update report data
        setReportData(response.data);

        // If report is completed or failed, stop polling
        if (["COMPLETED", "FAILED"].includes(response.data.status)) {
          clearInterval(interval);
          setPollingInterval(null);
        }
      } catch (err) {
        console.error("Error polling report status:", err);
        // If we get an error, stop polling
        clearInterval(interval);
        setPollingInterval(null);
        setError("Failed to check report status");
      }
    }, 3000);

    setPollingInterval(interval);
  };

  const handleDownload = () => {
    if (
      reportData &&
      reportData.reportId &&
      reportData.status === "COMPLETED"
    ) {
      adminReportService.downloadReport(reportData.reportId);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "QUEUED":
        return <Clock className="h-5 w-5 text-gray-500" />;
      case "PROCESSING":
        return <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />;
      case "COMPLETED":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "FAILED":
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "QUEUED":
        return "Queued";
      case "PROCESSING":
        return "Processing";
      case "COMPLETED":
        return "Ready for download";
      case "FAILED":
        return "Failed";
      default:
        return "Unknown";
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4">Generate User Report</h3>

      <form onSubmit={handleGenerateReport} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="startDate"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label
              htmlFor="endDate"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="format"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Report Format
          </label>
          <select
            id="format"
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            {formatOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        <div className="flex justify-between items-center">
          <button
            type="submit"
            disabled={loading}
            className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              loading ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            <FileText className="h-4 w-4 mr-2" />
            Generate Report
          </button>

          {reportData && reportData.status && (
            <div className="flex items-center">
              {getStatusIcon(reportData.status)}
              <span className="ml-2 text-sm text-gray-600">
                {getStatusText(reportData.status)}
              </span>
              {reportData.progress !== undefined &&
                reportData.status === "PROCESSING" && (
                  <span className="ml-2 text-xs text-gray-500">
                    ({Math.round(reportData.progress)}%)
                  </span>
                )}
            </div>
          )}
        </div>
      </form>

      {reportData && reportData.status === "COMPLETED" && (
        <div className="mt-6 border-t border-gray-200 pt-4">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="font-medium">Report Ready</h4>
              <p className="text-sm text-gray-600">
                {format} report for {userName} from{" "}
                {new Date(startDate).toLocaleDateString()} to{" "}
                {new Date(endDate).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={handleDownload}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserReportGenerator;
